import { makeAutoObservable } from "mobx";
import { ZUid } from "src/types/zUid";
import { Sheet2Store } from "src/components/Sheet2/Sheet2Store";
import { groupChecked, GroupChecked } from "src/common/groupChecked";
import { applyOrderStd, CtrlColumns } from "src/components/tables/TableStore";
import { ColumnKey } from "src/components/tables/ColumnPoor";
import { Bom2aColorInfo } from "./Bom2aPositionsTable/components/Bom2aColorCell/Bom2aColorModal";
import {
  Bom2aEdFields,
  Bom2aEdPosition,
  cvtPositions,
  EdBom2aColorModel,
  ZBom2aColorColumn,
  ZValue,
} from "./Bom2aPositionsTypes";
import {
  assignBom2aColor,
  createBom2aColorModel,
  createBom2aEmptyPosition,
  deleteBom2aPositions,
  loadBom2aPositions,
  loadDynamicColorColumns,
  saveBom2aFields,
} from "./apiBom2aPositions";
import { ZBom2a } from "../../Bom2aTypes";
import { bom2aColumns } from "./Bom2aPositionsTable/bom2aColumns";
import { Bom2aColumn } from "./Bom2aPositionsTable/Bom2aColumn";

/* eslint no-plusplus: "off" */
/* eslint no-restricted-syntax: "off" */

export class Bom2aPositionsStore implements CtrlColumns {
  constructor() {
    makeAutoObservable(this);
  }

  bomId: ZUid = "";

  setBomId(id: ZUid) {
    this.bomId = id;
  }

  colorColumns: ZBom2aColorColumn[] = [];

  setColorColumns(list: ZBom2aColorColumn[]) {
    // Пустые названия заменяются на дефолтные, чтобы не было пробелов в заголовке
    this.colorColumns = list.map(({ id, name }, i) => ({
      id,
      name: name || `Color ${i + 1}`,
    }));
  }

  async init(bom: ZBom2a) {
    this.sheetStore = new Sheet2Store();
    this.setBomId(bom.id);
    this.selectedIds.clear();
    this.collapsed.clear();
    await Promise.all([
      this.load(true),
      loadDynamicColorColumns(bom.id).then((list) =>
        this.setColorColumns(list),
      ),
    ]);
  }

  async load(isFirst: boolean) {
    this.setError(null);
    if (isFirst) this.setPositions([]);
    try {
      this.lock();
      const list = await loadBom2aPositions(this.bomId);
      this.setPositions(cvtPositions(list));
    } catch (e) {
      if (isFirst) {
        this.setError(e);
      } else {
        throw e;
      }
    } finally {
      this.unlock();
    }
  }

  positions: Bom2aEdPosition[] = [];

  setPositions(list: Bom2aEdPosition[]) {
    this.positions = list;
  }

  lockCounter = 0;

  lock() {
    this.lockCounter++;
  }

  unlock() {
    this.lockCounter--;
  }

  get buzy(): boolean {
    return this.lockCounter > 0;
  }

  error: Error | null = null;

  setError(e: Error | null) {
    this.error = e;
  }

  // selection

  selectedIds: Set<ZUid> = new Set();

  get selectedList(): ZUid[] {
    return Array.from(this.selectedIds);
  }

  togglePositionRow(id: ZUid) {
    if (this.selectedIds.has(id)) {
      this.selectedIds.delete(id);
    } else {
      this.selectedIds.add(id);
    }
  }

  toggleMaterial(material: Bom2aEdPosition, prevState: GroupChecked) {
    const needClear = prevState === "all";
    material.bomPositionRows.forEach(({ id }) => {
      if (needClear) {
        this.selectedIds.delete(id);
      } else {
        this.selectedIds.add(id);
      }
    });
  }

  materialSelection(material: Bom2aEdPosition): GroupChecked {
    return (
      groupChecked(material.bomPositionRows, (row) =>
        this.selectedIds.has(row.id),
      ) ?? "none"
    );
  }

  get totalSelection(): GroupChecked {
    return (
      this.positions.reduce(
        (acc: GroupChecked | undefined, mat) =>
          groupChecked(
            mat.bomPositionRows,
            ({ id }) => this.selectedIds.has(id),
            acc,
          ),
        undefined,
      ) ?? "none"
    );
  }

  toggleTotal(prevState: GroupChecked) {
    this.positions.forEach((mat) => this.toggleMaterial(mat, prevState));
  }

  // positions expanding

  collapsed = new Set<ZUid>();

  toggleCollapsing(materialTypeId: ZUid) {
    if (this.collapsed.has(materialTypeId)) {
      this.collapsed.delete(materialTypeId);
    } else {
      this.collapsed.add(materialTypeId);
    }
  }

  async updateTask(task: () => Promise<void>) {
    try {
      this.lock();
      await task();
      // заниматься кастомным обновлением данных - лишний код и путь к багам. Поэтому используем второй запрос
      await this.load(false);
    } finally {
      this.unlock();
    }
  }

  async createEmptyPosition(materialTypeId: ZUid) {
    await this.updateTask(async () => {
      await createBom2aEmptyPosition(this.bomId, materialTypeId);
    });
  }

  sheetStore = new Sheet2Store();

  updateFieldValue(rowId: ZUid, field: keyof Bom2aEdFields, value: ZValue) {
    for (const pos of this.positions) {
      for (const row of pos.bomPositionRows) {
        if (rowId === row.id) {
          row.fields[field].value = value;
        }
      }
    }
  }

  // eslint-disable-next-line class-methods-use-this
  async saveFieldValue(rowId: ZUid, fieldId: ZUid, value: string) {
    await saveBom2aFields({
      id: String(rowId),
      fields: [{ id: String(fieldId), value }],
    });
  }

  getRowPosition(rowId: ZUid): Bom2aEdPosition | null {
    for (const pos of this.positions) {
      const row = pos.bomPositionRows.find(({ id }) => id === rowId);
      if (row) return pos;
    }
    return null;
  }

  async saveColor(
    rowId: ZUid,
    colorModel: EdBom2aColorModel,
    data: Bom2aColorInfo,
  ) {
    await this.updateTask(async () => {
      const colorReference = data.colorReference?.[0] ?? null;
      if (colorModel.bomPositionColorModel.id) {
        const { fields } = colorModel.bomPositionColorModel;
        await saveBom2aFields({
          id: String(colorModel.bomPositionColorModel.id),
          fields: [
            { id: String(fields.colorManual.id), value: data.colorManual },
            { id: String(fields.colorReference.id), value: colorReference },
            { id: String(fields.comment.id), value: data.comment },
          ],
        });
        await assignBom2aColor(
          colorModel.bomPositionColorModel.id,
          data.color?.id,
        );
      } else {
        await createBom2aColorModel({
          params: {
            bomPositionId: rowId,
            colorModelId: colorModel.id,
            colorId: data.color?.id ?? undefined,
          },
          body: {
            colorManual: data.colorManual,
            colorReference,
            comment: data.comment,
          },
        });
      }
    });
  }

  get canDelete(): boolean {
    return this.selectedIds.size > 0 && !this.buzy;
  }

  deleteStatus: "deleting" | "open" | null = null;

  setDeleteStatus(status: Bom2aPositionsStore["deleteStatus"]) {
    this.deleteStatus = status;
  }

  startDelete() {
    this.setDeleteStatus("open");
  }

  async doDelete() {
    this.setDeleteStatus("deleting");
    try {
      await deleteBom2aPositions(Array.from(this.selectedIds) as string[]);
      this.selectedIds.clear();
      this.setDeleteStatus(null);
      await this.load(false);
    } catch (e) {
      this.setDeleteStatus("open");
      throw e;
    }
  }

  get columns() {
    return bom2aColumns(this);
  }

  get columnWidths(): string[] {
    return this.columns.map(({ minWidth, width }) => width ?? `${minWidth}px`);
  }

  get finalColumns(): Bom2aColumn[] {
    const visible = this.columns.filter(({ key }) => !this.hiddenSet.has(key));
    return applyOrderStd(this, visible);
  }

  columnOrder: string[] = [];

  readonly fixedLeftRaw: number = 0;

  hiddenSet = new Set<ColumnKey>();

  isColumnVisible(key: ColumnKey): boolean {
    return !this.hiddenSet.has(key);
  }

  setColumnVisible(key: ColumnKey, visible: boolean): void {
    if (visible) {
      this.hiddenSet.delete(key);
    } else {
      this.hiddenSet.add(key);
    }
  }

  // Это часть интерфейса CtrlColumn
  // eslint-disable-next-line class-methods-use-this
  canColumnHide(key: ColumnKey): boolean {
    return key !== "material";
  }

  setColumnOrder(newOrder: string[]): void {
    this.columnOrder = newOrder;
  }

  // Это часть интерфейса CtrlColumn
  // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
  setFixedLeftRaw(count: number): void {}

  get posMap(): Record<string, number> {
    const dict: Record<string, number> = {};
    this.finalColumns
      .filter(({ isCell }) => isCell)
      .forEach(({ key }, i) => {
        dict[key] = i;
      });
    return dict;
  }
}
